import { Button } from "@/components/ui/button";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  AccountSchema,
  NewAccountPayload,
  NewAccountRegisterPayload
} from "@/types/account.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { SubmitHandler, useForm } from "react-hook-form";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";

import HekaLoader from "@/assets/heka-loader.svg?react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import AuthLayout from "@/layout/auth.layout";
import { post } from "@/lib/http";
import { AlertCircle } from "lucide-react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

//TODO:
//Refacto all this stuff into smaller component
export function RegisterPage() {
  const [loading, setLoading] = useState<boolean>(false);
  const [err, setErr] = useState<string>("");

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const form = useForm<NewAccountPayload>({
    resolver: zodResolver(AccountSchema),
    defaultValues: {
      organizationName: "",
      firstName: "",
      familyName: "",
      username: "",
      email: "",
      password: "",
      passwordConfirmation: ""
    }
  });

  const onSubmit: SubmitHandler<NewAccountPayload> = async data => {
    setLoading(true);
    try {
      if (!recaptchaRef.current?.getValue()) {
        toast.error("ReCAPTCHA verification failed");
        return;
      }
      await post<{ success: boolean }, { token: string }>(`/api/auth/captcha`, {
        token: recaptchaRef.current?.getValue() ?? ""
      });

      const token = searchParams.get("token");

      await post<any, NewAccountRegisterPayload>("/api/auth/register", {
        token: token ? decodeURI(token).replace(/%2B/g, "+") : null,
        registerDto: data
      });

      setLoading(false);
      toast.success("Organization has been created !");
      navigate("/login");
    } catch (error: any) {
      recaptchaRef.current?.reset();
      setErr(error.message);
      setLoading(false);
      toast.error(error.message);
      throw err;
    }
  };

  const recaptchaRef = useRef<ReCAPTCHA>(null);

  return (
    <AuthLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card className="max-w-xl mx-auto">
            <CardHeader>
              <CardTitle className="text-xl">
                Register your organization{" "}
                <span className="font-bold text-primary">.</span>
              </CardTitle>
              <CardDescription>
                Enter your information to create an organization
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                {!!err && (
                  <Alert variant="destructive">
                    <AlertCircle className="w-4 h-4" />
                    <AlertTitle>Something Wrong Happened</AlertTitle>
                    <AlertDescription>{err}</AlertDescription>
                  </Alert>
                )}
                <div className="grid gap-2">
                  <FormField
                    control={form.control}
                    name="organizationName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Organization Name</FormLabel>
                        <FormControl>
                          <Input {...field} required />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>First Name</FormLabel>
                          <FormControl>
                            <Input {...field} required />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid gap-2">
                    <FormField
                      control={form.control}
                      name="familyName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Family Name</FormLabel>
                          <FormControl>
                            <Input {...field} required />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                <div className="grid gap-2">
                  <FormField
                    control={form.control}
                    name="username"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Username</FormLabel>
                        <FormControl>
                          <Input {...field} required />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid gap-2">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input {...field} required />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <Input type="password" required {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid gap-2">
                    <FormField
                      control={form.control}
                      name="passwordConfirmation"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password confirmation</FormLabel>
                          <FormControl>
                            <Input type="password" required {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={window.VITE_GOOGLE_CAPTCHA_SITE_KEY}
                  />
                </div>
                {!loading && (
                  <Button type="submit" className="w-full">
                    Create an organization
                  </Button>
                )}

                {loading && (
                  <Button className="w-full" disabled>
                    <HekaLoader className="max-h-full [--stroke:black]" />
                  </Button>
                )}
              </div>
              <div className="mt-4 text-sm text-center">
                Already have an account? <NavLink to="/login">Sign in</NavLink>
              </div>
            </CardContent>
          </Card>
        </form>
      </Form>
    </AuthLayout>
  );
}

export default RegisterPage;
